/*
 * Copyright (c) 2024-2025 Nodals AI Ltd
 * All rights reserved.
 */

import { InputConditions } from '../rules/index';

export type KeyValue = {
  key: string;
  value: string;
};

export type AdAssignedKeyValues = {
  id: string;
  kvs: KeyValue[];
};

export type PlacementAssignedKeyValues = {
  gad: string;
  kvs?: KeyValue[];
  ads?: AdAssignedKeyValues[];
};

export type CampaignEligibleAds = {
  id: string;
  edids: string[];
};

export type AdKeyValues = {
  id: string;
  edids: string[];
};

export type SelectionEndpointPlacementData = {
  gad: string;
  ss: { h: number; w: number }[];
  v: number;
  campaigns: CampaignEligibleAds[];
};

export type SelectionEndpointRequestPayload = {
  plcmts: SelectionEndpointPlacementData[];
};

export type SelectionEndpointResponsePayload = {
  plcmts: PlacementAssignedKeyValues[];
};

export type SelectionReportingPlacementData = {
  gad: string;
  ads: AdAssignedKeyValues[];
};

export type SelectionReportingEndpointRequestPayload = {
  plcmts: SelectionReportingPlacementData[];
};

export type AdRules = {
  conditions: InputConditions;
};

export type AdData = {
  id: string;
  geid?: string;
  rules: AdRules;
};

export type BidData = {
  advertiserDomains?: string[];
  bidder: string;
  cpm: number;
  dealId?: string;
  mediaType: 'banner' | 'video' | 'native';
};

export enum CampaignType {
  DIRECT = '0',
  DEAL = '1',
  LEARNING_DIRECT = '2',
  LEARNING_PROGRAMMATIC = '3',
  SCORING_DIRECT = '4',
  SCORING_DEAL = '5',
}

export enum AdIdType {
  EXTERNAL_ID = 'external_id',
  DELIVERY_ID = 'delivery_id',
}

export type CampaignData = {
  ads: AdData[];
  deals?: string[];
  id: string;
  geids?: string[];
  type: CampaignType;
  rules?: AdRules;
};

export type StoredData = {
  facts: Record<string, string>;
  property: PropertyConfig;
  campaigns: CampaignData[];
};

export enum SiteEnabledFeature {
  VSCORES = 'vscores',
}

export type PropertyConfig = {
  id: string;
  advid: string | null;
  acids: string[];
  dlail: string[];
  vscores: boolean;
};

export type RenderedAdData = {
  advertiserDomains?: string[];
  advertiserId?: string;
  lineItemId?: string;
  creativeId?: string;
  nodalsDealId?: string;
  responseIdentifier: string;
  isProgrammaticBuy: boolean;
  size?: PlacementSize;
};

type BaseEventAnalyticsData = {
  opportunityId: string;
  deliveryId: string;
  adUnit: string;
  pvid: string;
  sid: string;
  uid?: string;
  advertiserId?: string;
  advertiserDomains?: string[];
  lineItemId?: string;
  nodalsDealId?: string;
};

export type GenericEventAnalyticsData = BaseEventAnalyticsData & {
  impressionId: string;
};

export type ImpressionAnalyticsData = BaseEventAnalyticsData & {
  id: string;
  kvs?: KeyValue[];
  creativeId?: string;
  renderedData?: { size: PlacementSize };
};

export type PlacementSize = { width: number; height: number } | null;
